import type { ReactNode } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { useMutation } from '@tanstack/react-query'
import { setTimeout } from 'core-js'
import axiosClient from '@/api/client/axios'
import type { IAjaxResponse } from '@/api/interfaces'
import { checkBeforeSignCrypto, useGetSaleRequestBankAccounts } from '@/api/kubik/monolith'
import { checkOrganizationForSigning } from '@/api/kubik/order/clients/axios/checkOrganizationForSigning'
import ErrorModalContent from '@/components/composed/GeneralErrorModalContent/GeneralErrorModalContent'
import { closeModal, openModal } from '@/components/composed/Modal/actions'
import { addModalToWellKnown } from '@/components/composed/Modal/constants'
import { ErrorModal, SignDocumentWithUkepModal, SignOfferWithUkepModal } from '@/components/composed/modals'
import { EDocumentType } from '@/components/composed/modals/DealAgreementModal/interfaces'
import { EInfoModalType } from '@/components/composed/modals/InfoModal/types'
import {
  confirmOnCloseSignDocumentsWithUkep,
  confirmOnCloseSignOffersWithUkep,
} from '@/components/composed/modals/SignDocumentsAccreditationWithUkepModal/helpers'
import type { ISignDocumentWithUkepModalOptions } from '@/components/composed/modals/SignDocumentWithUkepModal/types'
import type { IButtonProps } from '@/components/ui/Button/interfaces'
import { SaleRequestApiName } from '@/constants/outputs'
import { createAccreditation } from '@/logic/accreditation/actions'
import { isImpersonationUser, isUserAgrarian, isUserSupplier } from '@/logic/auth/reducer'
import { loadData } from '@/logic/data/actions'
import { getAgrarianOrder } from '@/logic/order/reducer'
import { EProfileRequestType } from '@/logic/profile/constants'
import { ESignType } from '@/logic/signs/selectSignType/types'
import { scrollToDetails } from '@/pages/Profiles/UserProfile/components/AgrarianSale/helpers'
import TenderWarningModal from '@/pages/Profiles/UserProfile/components/Tender/modals/TenderWarningModal/TenderWarningModal'
import type { RootState } from '@/redux/interfaces'
import { useLocation, useRouterApi } from '@/router/hooks'
import { ECommonRoute, EUserProfileRoute } from '@/types'
import { EAccreditationType } from '@/types/Accreditation'
import { EPlatformType } from '@/types/EPlatformType'
import type { ESaleRequestType } from '@/types/ESaleRequestType'
import { EHarvestSaleRequestStatus, type IHarvestSaleRequestStatusActions } from '@/types/HarvestSaleRequest'
import { EAgrarianOrderStatus } from '@/types/Order'
import { ESupplierSignType } from '@/types/Order/IAgrarianOrder'
import { downloadBlobFile, getFileNameFromResponse } from '@/utils/files'
import { getPlatformType } from '@/utils/getPlatformType'
import { CantSignMessageError } from './components/CantSignMessageError'
import type { IStatusModelActionsProps } from './interfaces'
import { EOrganizationDetailsStatus } from './interfaces'
import { messages } from './messages'

addModalToWellKnown({ SignOfferWithUkepModal, SignDocumentWithUkepModal, TenderWarningModal, ErrorModal })

const mapState = (state: RootState) => ({
  isAgrarian: isUserAgrarian()(state),
  isSupplier: isUserSupplier()(state),
  isImpersonated: isImpersonationUser()(state),
  order: getAgrarianOrder()(state),
})

export function useStatusModelButtons(config: Omit<IStatusModelActionsProps, 'isOrderClosed'>) {
  const dispatch = useDispatch()
  const history = useRouterApi()
  const [isLoadingButton, setIsLoadingButton] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const { origin } = useLocation()
  const { saleRequestType } = useParams<{ saleRequestType?: ESaleRequestType }>()
  const { isAgrarian, isSupplier, isImpersonated, order } = useSelector(mapState)
  const isImpersonatedSupplier = isImpersonated && isSupplier

  const {
    allowToEditOrder,
    shouldEditOrder,
    openRejectingOrderModal,
    changeOrderStatus,
    payOrderWithSberBusiness,
    openFeedback,
    openSupplies,
    openSupplyFormModal,
    status,
    entityType,
    id,
    version,
    organizationId,
    accreditationType,
    isConfirmationButtonDisabled = false,
    isOrderStatusChangingInProcess,
    paymentType,
  } = config

  const { mutateAsync } = useMutation({
    // @ts-expect-error
    mutationKey: [status?.actions?.download?.documentUrl],
    mutationFn: () => {
      if (!status?.actions?.download?.documentUrl) return null

      return axiosClient<Blob>({
        method: 'get',
        url: status.actions.download.documentUrl,
        params: { attachment: true },
        responseType: 'blob',
      })
    },
  })

  const downloadFile = useCallback(async () => {
    const timeout = setTimeout(() => {
      setIsLoadingButton(true)
    }, 300)

    try {
      const response = await mutateAsync()
      const data = response.data
      // todo remove type assertion, needed to make the transformation form fetch to axios types
      const filename = getFileNameFromResponse(response as unknown as IAjaxResponse<Blob>) || messages.downloadCheck

      if (data) {
        const file = data as Blob
        const blob = new Blob([file], { type: file.type })
        downloadBlobFile(blob, filename)
      }
    } catch (error) {
      console.warn(error)
    } finally {
      clearTimeout(timeout)
      setIsLoadingButton(false)
    }
  }, [mutateAsync])

  const waitDownloadLink = useCallback(() => {
    setIsDownloading(true)
    let isActive = true

    const interval = setInterval(() => {
      if (status?.actions?.download?.documentUrl) {
        clearInterval(interval)
        setIsDownloading(false)
        isActive = false
        downloadFile()
      }
    }, 10000)

    const cleanup = () => {
      if (isActive) {
        clearInterval(interval)
        setIsDownloading(false)

        dispatch(
          openModal({
            options: {
              dialogId: 'ErrorModal',
              shouldCloseOnMissingClick: false,
            },
            contentOptions: {
              onSupport: () => history.push(ECommonRoute.feedback),
              onConfirm: () => dispatch(closeModal()),
            },
          }),
        )
      }
    }

    setTimeout(cleanup, 5 * 60 * 1000)
  }, [status?.actions?.download?.documentUrl, downloadFile, dispatch, history])

  const onDownload = useCallback(async () => {
    if (!isDownloading && !status?.actions?.download?.documentUrl) {
      waitDownloadLink()

      return
    }

    downloadFile()
  }, [isDownloading, status?.actions?.download?.documentUrl, downloadFile, waitDownloadLink])

  const { actions, params, id: statusId } = status || {}
  const { needAccreditation } = params || {}
  const shouldGetAccreditation = !!needAccreditation && isAgrarian

  const areAccountsEnabled =
    statusId === EHarvestSaleRequestStatus.PREPAYMENT_BILL_WAITING ||
    statusId === EHarvestSaleRequestStatus.PREPAYMENT_BILL_WAITING_CRYPTO ||
    statusId === EHarvestSaleRequestStatus.PAYMENT_BILL_WAITING_CRYPTO ||
    statusId === EHarvestSaleRequestStatus.PAYMENT_BILL_WAITING

  const { data, isLoading: areAccountsLoading } = useGetSaleRequestBankAccounts(id, { query: { enabled: areAccountsEnabled } })

  const accounts = useMemo(() => {
    return areAccountsEnabled ? data?.data : undefined
  }, [areAccountsEnabled, data?.data])

  const shouldDisableSignButton = areAccountsEnabled && !areAccountsLoading && !accounts

  const openNoAccountsErrorModal = useCallback(
    (customChildren: ReactNode) => {
      dispatch(
        openModal({
          options: {
            dialogId: 'InfoModal',
            shouldCloseOnMissingClick: false,
          },
          contentOptions: {
            type: EInfoModalType.ERROR_RED,
            title: messages.noAccountsErrorModal.title,
            children: customChildren,
            buttons: [
              {
                textId: messages.noAccountsErrorModal.close,
                modifiers: ['full-width-mobile', 'outline-gray'],
              },
            ],
          },
        }),
      )
    },
    [dispatch],
  )

  const onSignCryptoClick = useCallback(async () => {
    const response = (await checkBeforeSignCrypto()) as {
      meta: {
        status: string
        message?: string
      }
    }

    const isError = response.meta.status !== 'ОК'
    const hasNotFilledOrganazationData = response.meta.status === EOrganizationDetailsStatus.ORGANIZATION_DETAILS_REQUIRED
    const cantSignMessage = isError ? response.meta.message : ''

    if (hasNotFilledOrganazationData) {
      dispatch(
        openModal({
          options: { dialogId: 'InfoModal', shouldCloseOnMissingClick: false },
          contentOptions: {
            type: EInfoModalType.ERROR_RED,
            title: messages.organizationDetailsRequired.title,
            // Так работают модалки
            children: () => CantSignMessageError(cantSignMessage),
            buttons: [
              {
                textId: messages.organizationDetailsRequired.toFeedback,
                modifiers: ['outline', 'full-width'],
                onClick: () => {
                  history.push({ pathname: ECommonRoute.feedback })
                },
              },
            ],
          },
        }),
      )

      return
    }

    if (areAccountsEnabled && !accounts?.length) {
      openNoAccountsErrorModal(ErrorModalContent)
    } else if (entityType === EProfileRequestType.ORDERS) {
      dispatch(
        openModal({
          options: {
            dialogId: 'SignOfferWithUkepModal',
            shouldCloseOnMissingClick: false,
            onConfirm: () => confirmOnCloseSignOffersWithUkep(dispatch),
          },
        }),
      )
    } else {
      dispatch(
        openModal({
          options: {
            dialogId: 'SignDocumentWithUkepModal',
            shouldCloseOnMissingClick: false,
            onConfirm: () => confirmOnCloseSignDocumentsWithUkep(dispatch),
          },
          contentOptions: {
            documentId: id,
            documentType: (actions as IHarvestSaleRequestStatusActions)?.signCrypto?.documentType,
            version,
            isDraggable: true,
            onSuccessAction: () => {
              if (saleRequestType) {
                dispatch(
                  loadData({
                    name: SaleRequestApiName[saleRequestType],
                    params: { id },
                    shouldLoadForcibly: true,
                  }),
                )
              }
            },
            accounts,
          } as ISignDocumentWithUkepModalOptions,
        }),
      )
    }
  }, [areAccountsEnabled, accounts, entityType, dispatch, history, openNoAccountsErrorModal, id, actions, version, saleRequestType])

  const cancel = useMemo(
    (): IButtonProps => ({
      isHidden: !actions?.cancel,
      children: actions?.cancel?.name,
      onClick: openRejectingOrderModal,
    }),
    [actions?.cancel, openRejectingOrderModal],
  )

  const feedback = useMemo(
    (): IButtonProps => ({
      isHidden: !actions?.feedback,
      children: actions?.feedback?.name,
      onClick: openFeedback || (() => window.open(origin + ECommonRoute.feedback)),
    }),
    [actions?.feedback, openFeedback, origin],
  )

  const sberPay = useMemo(
    (): IButtonProps => ({
      isHidden: !actions?.sberPay,
      children: actions?.sberPay?.name,
      onClick: payOrderWithSberBusiness,
    }),
    [actions?.sberPay, payOrderWithSberBusiness],
  )

  const edit = useMemo(
    (): IButtonProps => ({
      isHidden: !actions?.edit,
      isDisabled: shouldEditOrder,
      children: actions?.edit?.name,
      onClick: () => {
        allowToEditOrder()
        scrollToDetails()
      },
    }),
    [actions?.edit, shouldEditOrder, allowToEditOrder],
  )

  const download = useMemo(
    (): IButtonProps => ({
      isHidden: !actions?.download,
      children: actions?.download?.name,
      loaderProps: { isVisible: isLoadingButton || isDownloading },
      onClick: onDownload,
    }),
    [actions?.download, isLoadingButton, isDownloading, onDownload],
  )

  const supplySchedule = useMemo(
    (): IButtonProps => ({
      isHidden: !actions?.supplySchedule,
      children: actions?.supplySchedule?.name,
      onClick: openSupplies,
    }),
    [actions?.supplySchedule, openSupplies],
  )

  const addSupply = useMemo(
    (): IButtonProps => ({
      isHidden: !actions?.addSupply,
      children: actions?.addSupply?.name,
      onClick: openSupplyFormModal,
    }),
    [actions?.addSupply, openSupplyFormModal],
  )

  const checkOrganizationData = useCallback(
    async (onValid: () => void) => {
      const response = await checkOrganizationForSigning()
      const { isValid, missingFields } = response

      if (isValid) {
        onValid()
      } else {
        const preparedFields = missingFields?.join(', ')

        dispatch(
          openModal({
            options: {
              dialogId: 'TenderWarningModal',
              shouldCloseOnMissingClick: false,
            },
            contentOptions: {
              title: messages.warningModal.title,
              messages: [messages.warningModal.messageTitle, preparedFields],
              buttonTitle: messages.warningModal.buttonTitle,
              onButtonClick: () => {
                history.push(EUserProfileRoute.organizationData)
                dispatch(closeModal())
              },
            },
          }),
        )
      }
    },
    [dispatch, history],
  )

  // кнопка `confirm` нужна, если:
  // - пришёл соответствующий экшен от бэка, но при этом:
  //  - для агрария не требуется аккредитация;
  //  - заказ не в статусе `ACCREDITATION` и текущий юзер - не админ под поставщиком
  const isConfirmShown =
    actions?.confirm && !shouldGetAccreditation && !(isImpersonatedSupplier && status?.id === EAgrarianOrderStatus.ACCREDITATION)

  const confirm = useMemo(
    (): IButtonProps => ({
      isHidden: !isConfirmShown,
      isDisabled: shouldEditOrder || isConfirmationButtonDisabled,
      loaderProps: { isVisible: isOrderStatusChangingInProcess },
      children:
        status?.id === EAgrarianOrderStatus.SUPPLIER_PROPOSAL && order?.supplier.signMethod === ESupplierSignType.ANY
          ? messages.orderActions.selectTypeSign.button
          : actions?.confirm?.name,
      onClick: () => {
        if (order?.supplier.signMethod === ESupplierSignType.ANY || order?.supplier.signMethod === ESupplierSignType.UKEP) {
          checkOrganizationData(changeOrderStatus)
        } else {
          changeOrderStatus()
        }
      },
    }),
    [
      actions?.confirm,
      isConfirmShown,
      shouldEditOrder,
      isConfirmationButtonDisabled,
      isOrderStatusChangingInProcess,
      status?.id,
      order?.supplier.signMethod,
      changeOrderStatus,
      checkOrganizationData,
    ],
  )

  const openStartSignModal = useCallback(() => {
    dispatch(
      openModal({
        options: {
          dialogId: 'DealAgreementModal',
          isDraggable: true,
        },
        contentOptions: {
          entityType,
          id,
          version,
          paymentType,
          orderStatusId: statusId,
          documentType: statusId === EAgrarianOrderStatus.AGRARIAN_ASSIGNMENT_WAITING ? EDocumentType.ASSIGNMENT : EDocumentType.OFFER,
          accounts,
        },
      }),
    )
  }, [dispatch, entityType, id, version, paymentType, statusId, accounts])

  const sign = useMemo(
    (): IButtonProps => ({
      isHidden: !actions?.sign || order?.signMethod === ESignType.UKEP,
      isDisabled: shouldEditOrder || shouldDisableSignButton,
      children: actions?.sign?.name,
      onClick: () => {
        if (areAccountsEnabled && !accounts?.length) {
          openNoAccountsErrorModal(ErrorModalContent)

          return
        }

        if (order?.status.id === EAgrarianOrderStatus.SUPPLIER_OFFER_WAITING_SIMPLE) {
          checkOrganizationData(openStartSignModal)
        } else {
          openStartSignModal()
        }
      },
    }),
    [
      actions?.sign,
      order?.signMethod,
      order?.status.id,
      shouldEditOrder,
      shouldDisableSignButton,
      areAccountsEnabled,
      accounts,
      openNoAccountsErrorModal,
      checkOrganizationData,
      openStartSignModal,
    ],
  )

  const signCrypto = useMemo(
    (): IButtonProps => ({
      isHidden: !actions?.signCrypto || order?.signMethod === ESignType.PEP || !!order?.cryptoSignResult?.workflowId,
      isDisabled: shouldDisableSignButton,
      children: actions?.signCrypto?.name,
      onClick: () => checkOrganizationData(onSignCryptoClick),
    }),
    [actions?.signCrypto, onSignCryptoClick, order?.cryptoSignResult, order?.signMethod, shouldDisableSignButton, checkOrganizationData],
  )

  const continueSign = useMemo(
    (): IButtonProps => ({
      isHidden: !actions?.signCrypto || order?.signMethod === ESignType.PEP || !order?.cryptoSignResult?.workflowId,
      isDisabled: shouldDisableSignButton,
      children: 'Продолжить подписание',
      onClick: () => {
        window.location.assign(`/sign-document?workflowId=${order?.cryptoSignResult?.workflowId}`)
      },
    }),
    [actions?.signCrypto, order?.cryptoSignResult?.workflowId, order?.signMethod, shouldDisableSignButton],
  )

  const accreditation = useMemo(
    (): IButtonProps => ({
      isHidden: !shouldGetAccreditation,
      textId:
        accreditationType === EAccreditationType.REQUEST
          ? messages.requestActions.getAccreditation.button
          : messages.orderActions.getAccreditation.button,
      onClick: () => {
        if (organizationId) dispatch(createAccreditation({ organizationId, accreditationType }))
      },
    }),
    [shouldGetAccreditation, dispatch, organizationId, accreditationType],
  )

  const accreditationWithUKEP = useMemo(
    (): IButtonProps => ({
      isHidden: !actions?.addDocumentUKEP || getPlatformType() !== EPlatformType.DESKTOP,
      children: messages.orderActions.getAccreditationWithUKEP.button,
      onClick: () =>
        dispatch(
          openModal({
            options: {
              dialogId: 'SignDocumentsAccreditationWithUkepModal',
              onConfirm: () => confirmOnCloseSignDocumentsWithUkep(dispatch),
            },
          }),
        ),
    }),
    [dispatch, actions?.addDocumentUKEP],
  )

  // Проверено только для агрария, оплату по чеку проверить не получилось
  // PREPAYMENT_CONFIRMED, SHIPPING, PAYMENT_CONFIRMED без кнопок
  return {
    cancel, // AGRARIAN_PROPOSAL, SUPPLIER_PROPOSAL, ACCREDITATION, SUPPLIER_OFFER_WAITING_SIMPLE, SUPPLIER_OFFER_WAITING_CRYPTO, AGRARIAN_OFFER_WAITING, AGRARIAN_OFFER_WAITING_CRYPTO, AGRARIAN_ASSIGNMENT_WAITING, PREPAYMENT_WAITING
    feedback, //
    sberPay, //
    edit, //
    download, // PREPAYMENT_WAITING
    supplySchedule, // SHIPPING_PAYMENT
    addSupply, //
    confirm, // SUPPLIER_PROPOSAL, ACCREDITATION
    sign, // AGRARIAN_OFFER_WAITING, AGRARIAN_ASSIGNMENT_WAITING
    accreditation, //
    accreditationWithUKEP, // ACCREDITATION
    signCrypto, // AGRARIAN_OFFER_WAITING_CRYPTO
    continueSign,
  }
}
